import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../constants/environment';
import { useContext, useState } from 'react';
import SessionContext from '../contexts/SessionContext';

interface PullOpsTraxInput {
	organizationId: string;
}

interface PullStatusResponse {
	organizationId: string;
	target: string;
	status: 'pending' | 'in-progress' | 'completed' | 'failed' | null;
	locations: unknown[] | null;
	error: string | null;
}

export const usePullOpsTraxLocations = () => {
	const sessionContext = useContext(SessionContext);
	const [organizationId, setOrganizationId] = useState<string | null>(null);

	const submit = useMutation({
		mutationFn: async (input: PullOpsTraxInput) => {
			const { data } = await axios.post<{ organizationId: string }>(
				`${API_URL}/onboarding/pull-ops-trax-locations`,
				input,
				{ headers: { Authorization: sessionContext?.sessionId ?? '' } }
			);
			setOrganizationId(data.organizationId);
			return data;
		},
	});

	const status = useQuery({
		queryKey: ['pull-opstrax-status', organizationId],
		queryFn: async () => {
			const { data } = await axios.get<PullStatusResponse>(
				`${API_URL}/onboarding/pull-status`,
				{ params: { organizationId, target: 'opstrax' }, headers: { Authorization: sessionContext?.sessionId ?? '' } }
			);
			return data;
		},
		enabled: !!organizationId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (data?.status === 'completed' || data?.status === 'failed') return false;
			return 3000;
		},
	});

	const reset = () => setOrganizationId(null);

	return {
		submit,
		status,
		organizationId,
		reset,
		isPolling: !!organizationId && status.data?.status !== 'completed' && status.data?.status !== 'failed',
		isComplete: status.data?.status === 'completed',
		isFailed: status.data?.status === 'failed',
		error: status.data?.error ?? null,
	};
};

